import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  ColumnFiltersState,
  SortingState,
  Updater,
  VisibilityState
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useLocation, useSearchParams } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/constants/constants';
import type { SymbolKey } from '@/entities/symbol';
import { useForwardCatalog, useSpotCatalog } from '@/features/Catalog/api';
import { useAppSelector } from '@/hooks/redux-hooks';
import { energyTransitionModules, FILTER_KEYS, oilOnlyModules } from '@/pages/Catalog/constants';
import { useFilter } from '@/pages/Catalog/contexts/filter/useFilter';
import { arrEqualsAll } from '@/pages/Catalog/utils/filters';
import { takeItemBasedOnOrder } from '@/pages/Catalog/utils/takeItemBasedOnOrder';
import type { CatalogItem } from '@/types/catalog';
import getQueryStatus from '@/utils/getQueryStatus';

import { defaultVisibilityState, forwardCurvesColumns, spotColumns } from './constants';

const defaultColumn = {
  filterFn: arrEqualsAll
};

const getCatalogStatus = (query: {
  data?: CatalogItem[];
  isLoading: boolean;
  isError: boolean;
}) => {
  const status = getQueryStatus(query);
  if (status === 'fulfilled' && query.data === undefined) return 'loading';
  return status;
};

const SORT_PARAM = 'sort';

function parseSorting(searchParams: URLSearchParams, defaultSorting: SortingState): SortingState {
  const sortParam = searchParams.get(SORT_PARAM);
  if (!sortParam) return defaultSorting;
  const colonIdx = sortParam.lastIndexOf(':');
  if (colonIdx === -1) return defaultSorting;
  const id = sortParam.slice(0, colonIdx);
  const dir = sortParam.slice(colonIdx + 1);
  return [{ id, desc: dir === 'desc' }];
}

function serializeSorting(sorting: SortingState): string | null {
  if (!sorting.length) return null;
  const { id, desc } = sorting[0];
  return `${id}:${desc ? 'desc' : 'asc'}`;
}

const useCatalog = (isForwardCurves = false) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, disableSearchParams } = useFilter();
  const symbols = useAppSelector(state => state.symbolSelector.symbols);

  // Freeze the visible symbol set the moment the toggle turns ON; ignore selection
  // changes while the toggle is active so clearing + re-selecting doesn't shift the view.
  const frozenSymbolsRef = useRef<SymbolKey[]>([]);
  const prevShowSelectedRef = useRef(false);
  const showSelectedNow = filters[SHOW_SELECTED_URL_PARAM];
  if (showSelectedNow && !prevShowSelectedRef.current) {
    frozenSymbolsRef.current = symbols;
  }
  prevShowSelectedRef.current = showSelectedNow;

  const columns = isForwardCurves ? forwardCurvesColumns : spotColumns;

  const [activeTab, setActiveTab] = useState<number>(isForwardCurves ? 1 : 0);
  const [showDiscontinued, setShowDiscontinued] = useState<boolean>(false);
  const [expandedData, setExpandedData] = useState<boolean>(false);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultVisibilityState);

  // Modal (disableSearchParams) uses local sorting state; main page uses URL
  const defaultSorting = useMemo<SortingState>(
    () => (isForwardCurves ? [] : [{ id: 'Alias', desc: false }]),
    [isForwardCurves]
  );
  const [localSorting, setLocalSorting] = useState<SortingState>(defaultSorting);

  const sorting = useMemo(
    () => (disableSearchParams ? localSorting : parseSorting(searchParams, defaultSorting)),
    [disableSearchParams, localSorting, searchParams, defaultSorting]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (disableSearchParams) {
        setLocalSorting(next);
      } else {
        setSearchParams(
          prev => {
            const params = Object.fromEntries(prev.entries());
            const sortStr = serializeSorting(next);
            if (sortStr) {
              params[SORT_PARAM] = sortStr;
            } else {
              delete params[SORT_PARAM];
            }
            return params;
          },
          { replace: true }
        );
      }
    },
    [sorting, disableSearchParams, setSearchParams]
  );

  const spotCatalogQuery = useSpotCatalog();
  const forwardCatalogQuery = useForwardCatalog();

  const spotStatus = getCatalogStatus(spotCatalogQuery);
  const forwardStatus = getCatalogStatus(forwardCatalogQuery);
  const status = isForwardCurves ? forwardStatus : spotStatus;
  const catalog = isForwardCurves ? forwardCatalogQuery.data : spotCatalogQuery.data;

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Route changes reset catalog-local UI state.
  useEffect(() => {
    clearState();
    setActiveTab(isForwardCurves ? 1 : 0);
  }, [location.pathname]);

  const clearState = useCallback(() => {
    setFilterOpen(false);
    setColumnVisibility(prevState => ({
      ...prevState,
      TimeRef: isForwardCurves || expandedData,
      PeriodType: isForwardCurves || expandedData
    }));
  }, [isForwardCurves, expandedData]);

  const filteredData = useMemo(() => {
    if (!catalog) return [];
    if (expandedData || isForwardCurves) return catalog;

    return Object.values(
      catalog.reduce((acc: Partial<Record<string, CatalogItem>>, item) => {
        const existing = acc[item.Code];
        if (existing) {
          acc[item.Code] = takeItemBasedOnOrder(existing, item) as CatalogItem;
        } else {
          acc[item.Code] = item;
        }
        return acc;
      }, {})
    );
  }, [catalog, expandedData, isForwardCurves]);

  // Derive controlled columnFilters from URL/local FilterContext state
  const showSelected = showSelectedNow;
  const only = filters.only;

  const columnFilters = useMemo((): ColumnFiltersState => {
    const result: ColumnFiltersState = [
      { id: 'Status', value: showDiscontinued },
      { id: 'Code', value: showSelected ? frozenSymbolsRef.current : [] }
    ];

    // Regular column filters (IndexModule handled separately due to 'only' preset logic)
    for (const key of Object.values(FILTER_KEYS)) {
      if (key === FILTER_KEYS.INDEX_MODULE) continue;
      const value = filters[key as keyof typeof filters];
      if (value && typeof value === 'string') {
        result.push({ id: key, value: [value] });
      }
    }

    // IndexModule: direct filter takes priority over 'only' presets
    const indexModuleValue = filters[FILTER_KEYS.INDEX_MODULE];
    if (indexModuleValue) {
      result.push({ id: 'IndexModule', value: [indexModuleValue] });
    } else if (only === 'oil') {
      result.push({ id: 'IndexModule', value: oilOnlyModules });
    } else if (only === 'energyTransition') {
      result.push({ id: 'IndexModule', value: energyTransitionModules });
    }

    return result;
  }, [filters, showSelected, only, showDiscontinued]);

  const globalFilter = filters.search ?? '';

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- TanStack Table is the catalog table engine.
  const table = useReactTable({
    data: filteredData,
    columns,
    defaultColumn,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    globalFilterFn: 'includesString',
    state: {
      columnVisibility,
      sorting,
      columnFilters,
      globalFilter
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange
  });

  return {
    status,
    filterOpen,
    setFilterOpen,
    filteredData,
    table,
    activeTab,
    setActiveTab,
    isForwardCurves,
    expandedData,
    setExpandedData,
    showDiscontinued,
    setShowDiscontinued
  };
};

export default useCatalog;
